<template>
  <!--
    Usage (vue-multiselect slot, first item in the dropdown):

    <div slot="beforeList">
      <multiselect-region-selector
        :all-regions="destinationRegion.choices"
        :selected-regions.sync="destinationRegion.value" />
    </div>
  -->

  <div>
    <div @click="handleModalShowClick" class="region-selector">
      <i class="fa fa-lg fa-globe region-selector__icon"></i>
      <p class="region-selector__title">Select zones on the map</p>
    </div>

    <b-modal-custom
      title="Zone selecting tool"
      :modal-class.sync="$options.data.themeClass"
      size="lg"
      v-model="modalDisplay"
      ok-title="Apply"
      @shown="handleModalShown"
      @ok="handleOk"
      @cancel="handleCancel">

      <base-map :map-center="map.center" :map-zoom="map.zoom">
        <template #map-extras>

          <l-geo-json v-for="(zone, index) in zones"
            :geojson="zone.geoJSON"
            :key="`zone-${index}`"
            :options="getGeoJSONLayerOptions(zone)"
            @ready="evt => handlePolygonReady(evt, zone)"
            @mouseover="evt => handlePolygonMouseOver(evt, zone)"
            @mouseout="evt => handlePolygonMouseOut(evt, zone)"
            @click="evt => handlePolygonMouseClick(evt, zone)">

          </l-geo-json>

        </template>
      </base-map>

    </b-modal-custom>

  </div>
</template>

<script>
import { cloneDeep } from 'lodash-es'
import {
  LGeoJson,
} from 'vue2-leaflet'
import BModalCustom from '@/base_components/BModalCustom'
import BaseMap from '@/base_components/BaseMap';

export default {
  name: 'multiselect-region-selector',

  components: {
    BaseMap,
    BModalCustom,
    LGeoJson,
  },

  props: {
    allRegions: {
      type: Array,
      default: () => [],
      required: true,
    },
    selectedRegions: {
      type: Array,
      default: () => [],
      required: true,
    },
  },

  data() {
    return {
      modalDisplay: false,
      zones: [],

      map: {
        zoom: 1,
        center: [40.297979, -5.681251],

        styles: {
          selectedRegion: {
            weight: 3,
            color: '#17a30e',
            dashArray: '',
            fillOpacity: 0.4
          },
          selectedRegionHover: {
            weight: 3,
            color: '#48c640',
            dashArray: '',
            fillOpacity: 0.4
          },
          unselectedRegion: {
            weight: 2,
            color: '#2E93FA',
            dashArray: '',
            fillOpacity: 0.1
          },
          unselectedRegionHover: {
            weight: 3,
            color: '#59b7e3',
            dashArray: '',
            fillOpacity: 0.4
          },
        },
      },
    };
  },

  methods: {
    handleModalShowClick() {
      this.modalDisplay = true;
    },

    handleModalShown() {
      // it's required to set url explicitly since user can switch theme after region selector is mounted,
      // this case it remembers the previous variant
      // this.map.url = getMapMode(this.$store.state.gui.darkMode ? 'dark' : 'light');

      // this.map.mapObject.invalidateSize();
      this.updateZones();
    },

    handleMapReady(mapObject) {
      // all map operations must be performed after <b-modal> displayed!
      this.map.mapObject = mapObject;
    },

    updateZones() {
      this.zones = this.getFormattedZones();
    },

    getFormattedZones() {
      const zones = this.allRegions.map(item => {
        let selected = this.selectedRegions.find(
          selectedRegion => selectedRegion.value === item.value
        );
        return {...item, selected: !!selected};
      });

      // to prevent props objects modifying
      return cloneDeep(zones);
    },

    updateZoneStyle(zone, layer) {
      if(zone.selected) {
        layer.setStyle(this.map.styles.selectedRegion);
      } else {
        layer.setStyle(this.map.styles.unselectedRegion);
      }
    },

    handlePolygonReady(evt, zone) {
      evt.bindTooltip(
        zone.text,
        {
          className: 'map-tooltip',
          permanent: false,
        },
      );
    },

    handlePolygonMouseOver({target: layer}, zone) {
      // console.log(layer.options);
      if(zone.selected) {
        layer.setStyle(this.map.styles.selectedRegionHover);
      } else {
        layer.setStyle(this.map.styles.unselectedRegionHover);
      }
    },

    handlePolygonMouseOut({target: layer}, zone) {
      this.updateZoneStyle(zone, layer);
    },

    handlePolygonMouseClick({target: layer}, zone) {
      zone.selected = !zone.selected;
      this.updateZoneStyle(zone, layer);
    },

    handleOk() {
      this.$emit(
        'update:selected-regions',
        this.zones.filter(zone => zone.selected)
      );
      this.$emit('input');
      this.modalDisplay = false;
    },

    handleCancel() {
      this.modalDisplay = false;
    },

    getGeoJSONLayerOptions(zone) {
      return {
        zone,
        style: this.getZoneStyle(zone),
      };
    },

    getZoneStyle(zone) {
      if(zone.selected) {
        return this.map.styles.selectedRegion;
      } else {
        return this.map.styles.unselectedRegion;
      }
    },
  },
}
</script>

<style lang="scss">
.map-tooltip {
  font-size: 12px;
  font-weight: 500;
}
</style>

<style scoped lang="scss">
.map-container {
  width: 100%;
  height: 400px;
}
</style>
